import { useQueryClient } from '@tanstack/react-query';
import { type ContentObject, type ContentObjectList, isJournalNote } from 'models';
import type { InfiniteData } from 'query';
import { useMemo } from 'react';
import { getContentObjectId, getContentObjectIdSafe } from '../../utils';
import {
  getBookmarksFeedInfiniteQueryKey,
  getContentObjectDetailQueryKey,
  getContentObjectsInfiniteQueryKey,
  getFeedKey,
  getProgramFeedKey,
} from '../keys';

type FeedRoutingFilter = {
  communityId?: string;
  feedKey?: string;
  groupId?: string;
  pinned?: boolean;
};

const getFeedRoutingContext = (contentObject: ContentObject): FeedRoutingFilter => {
  const [community] = contentObject.postInCommunities ?? [];
  const groupId = 'group' in contentObject ? contentObject.group.id : undefined;
  const programId = 'program' in contentObject ? contentObject.program.id : undefined;
  const programTopicId = 'programsTopic' in contentObject ? contentObject.programsTopic.id : undefined;

  return {
    communityId: community?.id,
    feedKey: programId && programTopicId ? getProgramFeedKey({ programId, programTopicId }) : undefined,
    groupId,
    pinned: contentObject.pinned,
  };
};

const getFeedRoutingFilter = (queryKey: readonly unknown[]) =>
  queryKey.find(
    (segment): segment is { filter?: FeedRoutingFilter } =>
      typeof segment === 'object' && segment !== null && 'filter' in segment,
  )?.filter;

const isPinnedFilterQuery = (queryKey: readonly unknown[]) => getFeedRoutingFilter(queryKey)?.pinned !== undefined;

const matchesFeedRouting = (queryKey: readonly unknown[], contentObject: ContentObject) => {
  const isJournalQuery = queryKey[1] === 'journal';

  if (isJournalQuery !== isJournalNote(contentObject)) {
    return false;
  }

  const filter = getFeedRoutingFilter(queryKey);

  if (!filter) {
    return true;
  }

  const context = getFeedRoutingContext(contentObject);

  return (
    (filter.communityId === undefined || filter.communityId === context.communityId) &&
    (filter.feedKey === undefined || filter.feedKey === context.feedKey) &&
    (filter.groupId === undefined || filter.groupId === context.groupId) &&
    (filter.pinned === undefined || filter.pinned === context.pinned)
  );
};

/**
 * @deprecated This manual cache-patching only exists because of the JS-to-TSX port; don't take it as
 * inspiration for new code. The single place feed/list and detail caches get patched after
 * create/edit/delete/like/bookmark, matching content objects via `getContentObjectId`.
 */
export const useContentObjectCache = () => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const detailQueryKeys = (id: string) => [getContentObjectDetailQueryKey({ id })];
    const listQueryKeys = [getFeedKey(), getContentObjectsInfiniteQueryKey({})];

    const mapLists = (id: string, updater: (item: ContentObjectList) => ContentObjectList) => {
      for (const queryKey of listQueryKeys) {
        queryClient.setQueriesData({ queryKey }, (oldData?: InfiniteData<ContentObjectList[]>) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) =>
              page.map((item) => (getContentObjectIdSafe(item) === id ? updater(item) : item)),
            ),
          };
        });
      }
    };

    const filterLists = (id: string) => {
      for (const queryKey of [...listQueryKeys, getBookmarksFeedInfiniteQueryKey({})]) {
        queryClient.setQueriesData({ queryKey }, (oldData?: InfiniteData<ContentObjectList[]>) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => page.filter((item) => getContentObjectIdSafe(item) !== id)),
          };
        });
      }
    };

    return {
      create(contentObject: ContentObject) {
        for (const queryKey of listQueryKeys) {
          queryClient.setQueriesData(
            { predicate: (query) => matchesFeedRouting(query.queryKey, contentObject), queryKey },
            (oldData?: InfiniteData<ContentObjectList[]>) => {
              if (!oldData) {
                return oldData;
              }

              const [firstPage, ...restOfPages] = oldData.pages;

              return {
                ...oldData,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: fix, ContentObjectList doesn't cover all rails content object types yet
                pages: [[contentObject as unknown as ContentObjectList, ...(firstPage ?? [])], ...restOfPages],
              };
            },
          );
        }
      },

      patch<T extends ContentObject>(contentObject: T, updater: (contentObject: T) => T) {
        const id = getContentObjectId(contentObject);

        for (const queryKey of detailQueryKeys(id)) {
          queryClient.setQueriesData<T>({ queryKey }, (cached) => (cached ? updater(cached) : cached));
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- generic updater is applied uniformly to every content-object type
        mapLists(id, (item) => updater(item as T) as unknown as ContentObjectList);
      },

      remove(contentObject: ContentObject) {
        const id = getContentObjectId(contentObject);

        for (const queryKey of detailQueryKeys(id)) {
          queryClient.removeQueries({ queryKey });
        }

        filterLists(id);
      },

      replace(contentObject: ContentObject) {
        const id = getContentObjectId(contentObject);

        for (const queryKey of detailQueryKeys(id)) {
          queryClient.setQueryData(queryKey, contentObject);
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: fix, ContentObjectList doesn't cover all rails content object types yet
        mapLists(id, () => contentObject as unknown as ContentObjectList);
      },

      syncPinnedFeeds(contentObject: ContentObject) {
        const id = getContentObjectId(contentObject);

        for (const queryKey of listQueryKeys) {
          queryClient.setQueriesData(
            {
              predicate: (query) =>
                isPinnedFilterQuery(query.queryKey) && !matchesFeedRouting(query.queryKey, contentObject),
              queryKey,
            },
            (oldData?: InfiniteData<ContentObjectList[]>) => {
              if (!oldData) {
                return oldData;
              }

              return {
                ...oldData,
                pages: oldData.pages.map((page) => page.filter((item) => getContentObjectIdSafe(item) !== id)),
              };
            },
          );

          queryClient.setQueriesData(
            {
              predicate: (query) =>
                isPinnedFilterQuery(query.queryKey) && matchesFeedRouting(query.queryKey, contentObject),
              queryKey,
            },
            (oldData?: InfiniteData<ContentObjectList[]>) => {
              if (!oldData || oldData.pages.flat().some((item) => getContentObjectIdSafe(item) === id)) {
                return oldData;
              }

              const [firstPage, ...restOfPages] = oldData.pages;

              return {
                ...oldData,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: fix, ContentObjectList doesn't cover all rails content object types yet
                pages: [[contentObject as unknown as ContentObjectList, ...(firstPage ?? [])], ...restOfPages],
              };
            },
          );
        }
      },
    };
  }, [queryClient]);
};
